import { baseApi } from 'services';
import { getSmartFlags, fetchFooterInfo } from '../../redux-core/overview/actions';
import { refetchCueContainers } from '../../redux-core/cueContainers/actions';

export type TGetRequiredRightsRequest = {
  cueContainerId?: number;
  subprojectId?: number;
  clearanceId?: number;
};

export type TRightsOptionsGroupRights = {
  id: number;
  subprojectId?: number;
  cueContainerId?: number;
  territoryId: number;
  territory: {
    id: number;
    name: string;
  };
  term: 1 | 2 | 3 | 4;
  descriptionOnLetters: string;
  otherDetails: string;
  start?: string;
  end?: string;
};

export type TRightsOptionsGroup = {
  id: number;
  name: string;
  type: 'rights' | 'options';
  descriptionOnLetters: string;
  defaultQuote?: number;
  approvedFees: number;
  rights: TRightsOptionsGroupRights[];
};

export type TGetRequiredRightsResponse = {
  rights: TRights[];
  name: string;
  descriptionOnLetters?: string;
};

export enum RightsTerm {
  NONE = 0,
  ONE_DAY = 1,
  DATE_RANGE = 2,
  IN_PERPETUITY = 3,
  OTHER = 4,
}

export type TRights = {
  subprojectId?: number;
  cueContainerId?: number;
  qclearClearanceId?: number;
  territoryId: number;
  territory: {
    name: string;
  };
  term: RightsTerm;
  start?: string;
  end?: string;
  otherDetails: string;
  descriptionOnLetters: string;
};

export type TUpsertRightsRequest = {
  clearanceId: number;
  name: string;
  descriptionOnLetters: string;
  defaultQuote?: number;
  territories: {
    id: number;
    start: string;
    end: string;
    term: 0 | 1 | 2 | 3 | 4;
    otherDetails: string;
  }[];
};

export type Territory = {
  id: number;
  start?: string;
  end?: string;
  term: 0 | 1 | 2 | 3 | 4;
  otherDetails: string;
};

export type UpsertRightsRequest = {
  overwriteExistingClearancesRights?: boolean;
  id?: number;
  cueContainerId?: number;
  subprojectId?: number;
  clearanceId?: number;
  type: 'rights' | 'options';
  name: string;
  territories: Territory[];
  descriptionOnLetters?: string;
  defaultQuote?: number;
};

export type InsertMultipleRightsRequest = {
  rightsOptionsGroups: UpsertRightsRequest[];
};

export type DeleteProductionRightsRequest = {
  id: number;
};

export type SyncTracksAndProductionRightsRequest = {
  productionId?: number;
  cueContainerId?: number;
};

export const rightsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProductionRights: builder.query<TRightsOptionsGroup[], { productionId?: number; cueContainerId?: number }>({
      query: (body) => ({
        url: 'clear/rights/get-production-rights',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['getProductionRights'],
    }),
    deleteProductionRights: builder.mutation<void, DeleteProductionRightsRequest>({
      query: (body) => ({ url: 'clear/rights/delete-production-rights', method: 'POST', body }),
      invalidatesTags: ['getProductionRights'],
    }),
    insertMultipleProductionRights: builder.mutation<void, InsertMultipleRightsRequest>({
      query: (body) => ({ url: 'clear/rights/insert-multiple', method: 'POST', body }),
      invalidatesTags: ['getProductionRights'],
    }),
    syncTracksAndProductionRights: builder.mutation<void, SyncTracksAndProductionRightsRequest>({
      query: (body) => ({ url: 'clear/rights/sync-tracks-and-production', method: 'POST', body }),
      invalidatesTags: ['clearanceBundle', 'getBundleLicenses', 'getTrackClearanceStatus'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(fetchFooterInfo());
        dispatch(refetchCueContainers());
      },
    }),
    updateProductionRights: builder.mutation<void, UpsertRightsRequest>({
      query: (body) => ({ url: 'clear/rights/update-production-rights', method: 'POST', body }),
      invalidatesTags: ['getProductionRights'],
    }),
    getRequiredRights: builder.query<TGetRequiredRightsResponse, TGetRequiredRightsRequest>({
      query: (body) => ({
        url: 'clear/rights/get-required-rights',
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: 0,
    }),
    upsertRights: builder.mutation<void, TUpsertRightsRequest>({
      query: (body) => ({ url: 'clear/rights/upsert', method: 'POST', body }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(getSmartFlags());
      },
    }),
  }),
});

export const {
  useGetProductionRightsQuery,
  useGetRequiredRightsQuery,
  useInsertMultipleProductionRightsMutation,
  useDeleteProductionRightsMutation,
  useSyncTracksAndProductionRightsMutation,
  useUpdateProductionRightsMutation,
  useUpsertRightsMutation,
} = rightsApi;
